import React, { useContext, useEffect, useState, useMemo } from 'react'
import {
    Typography,
    Stack,
    Box,
    CircularProgress,
    Button,
    Alert,
    Snackbar,
    Divider,
    FormControlLabel,
    Checkbox,
    Card,
    Autocomplete,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel
} from '@mui/material'
import { AddOutlined } from '@mui/icons-material'
import { EmploymentsContext } from '../../contexts/EmploymentsContext'
import SubmitSvg from '../SubmitShift/SubmitSvg'
import { isMobile } from '../../Utilities/isMobile'
import { useParams } from 'react-router-dom'
import { PATHS } from '../../constants'
import { AxiosWithAuth, authenticationService } from '../../Utilities/authenticationService'
import {
    getTextField,
    getNumberField,
    getEmpCdField,
    getEmploymentTypeSelect,
    EmploymentCard,
    AdminManagerButtons,
    UserActionMenu,
    handleSave,
    fetchUserData,
    useEditingState
} from '../../Utilities/userManagementUtils'

const AdminUserManagementEdit = ({ history }) => {
    const { userEmpId, userId } = useParams()
    const employmentId = localStorage.getItem('selected_emp')
    const { isAdmin, fetchAndSetEmployments } = useContext(EmploymentsContext)
    const [info, setInfo] = useState()
    const [originalInfo, setOriginalInfo] = useState()
    const [employments, setEmployments] = useState()
    const [originalEmployments, setOriginalEmployments] = useState()
    const [editing, setEditing] = useState(false)
    const [snackbarMessage, setSnackbarMessage] = useState('')
    const [snackbarSeverity, setSnackbarSeverity] = useState('success')
    const [errors, setErrors] = useState({})
    const [isLoading, setIsLoading] = useState(false)
    const [newPass, setNewPass] = useState(null)
    const [storeList, setStoreList] = useState([])
    const [addingStore, setAddingStore] = useState(false)
    const [selectedStore, setSelectedStore] = useState(null)
    const [companies, setCompanies] = useState([])
    const [loadError, setLoadError] = useState(null)

    const filteredStores = useMemo(() => {
        const stores = Array.isArray(storeList) ? storeList : []
        return stores.filter(store => !employments?.some(e => (e.store_id ?? e.store?.id) === store.id))
    }, [storeList, employments])

    const isMasterAdmin = authenticationService.currentUserValue?.is_admin

    const resetUserState = () => {
        setInfo(undefined)
        setOriginalInfo(undefined)
        setEmployments(undefined)
        setOriginalEmployments(undefined)
        setEditing(false)
        setErrors({})
        setLoadError(null)
        setAddingStore(false)
        setSelectedStore(null)
        setNewPass(null)
    }

    const fetch = async (isActive = () => true) => {
        setLoadError(null)
        try {
            await fetchUserData(
                userId,
                userEmpId,
                value => isActive() && setInfo(value),
                value => isActive() && setOriginalInfo(value),
                value => isActive() && setEmployments(value),
                value => isActive() && setOriginalEmployments(value),
                value => isActive() && setStoreList(value)
            )
        } catch (err) {
            if (!isActive()) return
            const status = err?.response?.status
            const message = status === 401 || status === 403
                ? 'このユーザーを表示する権限がありません。'
                : status === 404
                    ? 'ユーザーが見つかりませんでした。'
                    : 'ユーザー情報の読み込みに失敗しました。'
            setLoadError(message)
        }
    }

    const canEditTargetUser = () => {
        // Admins can edit all users - this is the AdminUserManagementEdit page
        return true
    }

    const handleAddStore = async () => {
        if (!selectedStore) return
        await AxiosWithAuth.post('/employments', {
            user_id: userId,
            store_id: selectedStore.id,
            role: info.role,
            is_manager: info.is_manager,
            rank: info.rank,
            open_close_duty: info.open_close_duty
        })
        setAddingStore(false)
        setSelectedStore(null)
        fetch()
    }

    const handleDeleteEmployment = async (employment) => {
        await AxiosWithAuth.delete('/employments/' + employment.id)
        if (String(employment.id) === localStorage.getItem('selected_emp')) {
            const remaining = employments.filter(emp => emp.id !== employment.id)
            if (remaining.length > 0) {
                await AxiosWithAuth.put('/users/me', { selected_emp: remaining[0].id })
                localStorage.setItem('selected_emp', remaining[0].id)
            }
        }
        fetchAndSetEmployments()
        fetch()
    }

    const handleSaveClick = () => {
        handleSave(employments, originalEmployments, info, originalInfo, errors, setErrors, setSnackbarMessage, userId, handleReturn)
    }

    const handleReturn = () => {
        history.push(PATHS.adminUserManagements)
    }

    useEditingState(info, originalInfo, employments, originalEmployments, setEditing)

    useEffect(() => {
        let active = true
        const isActive = () => active

        resetUserState()
        fetch(isActive)

        if (isMasterAdmin) {
            AxiosWithAuth.get('/companies')
                .then(res => {
                    if (active) setCompanies(res.data)
                })
                .catch(err => console.error(err))
        }

        return () => {
            active = false
        }
    }, [userId, userEmpId])

    if (loadError) {
        return (
            <Box sx={{ mt: '2rem', mx: 'auto', maxWidth: '32rem' }}>
                <Alert severity="error">{loadError}</Alert>
                <Button variant="outlined" sx={{ mt: 2 }} onClick={handleReturn}>
                    ユーザー管理に戻る
                </Button>
            </Box>
        )
    }

    if (!info) {
        return <CircularProgress sx={{ mx: '50%', mt: '10%' }} />
    }

    if (newPass)
        return (
            <div
                style={{
                    height: 'calc(100vh - 56px)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    margin: '0 1rem'
                }}
            >
                <SubmitSvg />
                <Typography variant="header1" sx={{ fontSize: '20px' }}>
                    新しいパスワード: {newPass}
                </Typography>
                <Button
                    onClick={() => {
                        setNewPass(null)
                    }}
                    variant="contained"
                    color="paloBlue"
                    fullWidth
                    sx={{ mt: 'auto', mb: '2rem' }}
                >
                    完了
                </Button>
            </div>
        )

    // Extra bottom padding so admin actions (e.g. password reset) stay above the fixed save bar
    const editFormBottomPadding = isMobile() ? '10rem' : '6.5rem'

    return (
        <Box sx={{ pb: editFormBottomPadding }}>
            <Box className="input-box shrinkable">
                <Snackbar
                    open={snackbarMessage !== ''}
                    autoHideDuration={4000}
                    onClose={() => { setSnackbarMessage(''); setSnackbarSeverity('success'); }}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert severity={snackbarSeverity} onClose={() => { setSnackbarMessage(''); setSnackbarSeverity('success'); }}>{snackbarMessage}</Alert>
                </Snackbar>
            </Box>
            {!info.confirmed_at && <Alert severity="error">メールアドレスの本人確認が未完了です。</Alert>}
            {info.deactivated_at && <Alert severity="error">アカウントが無効になっています。</Alert>}
            <Stack direction={isMobile() ? "column" : "row"} spacing={2} sx={{ mt: '1rem' }}>
                <Card sx={{ p: isMobile() ? 3 : 2, width: isMobile() ? '90vw' : '48%', boxShadow: isMobile() && 'none'}} >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">ユーザー情報</Typography>
                        <UserActionMenu
                            info={info}
                            userId={userId}
                            setSnackbarMessage={setSnackbarMessage}
                            setSnackbarSeverity={setSnackbarSeverity}
                            fetch={fetch}
                            setIsLoading={setIsLoading}
                            history={history}
                            employmentId={employmentId}
                            isAdmin={isAdmin}
                        />
                    </Box>
                    <Stack spacing={2} sx={{ flexWrap: 'wrap'}}>
                        <Stack direction={isMobile() ? "column" : "row"} spacing={2}>
                            {getTextField('せい(かな)', 'name_last', info, setInfo, errors, setErrors)}
                            {getTextField('めい(かな)', 'name_first', info, setInfo, errors, setErrors)}
                        </Stack>
                        <Stack direction={isMobile() ? "column" : "row"} spacing={2}>
                            {getTextField('姓(漢字)', 'kanji_last', info, setInfo, errors, setErrors)}
                            {getTextField('名(漢字)', 'kanji_first', info, setInfo, errors, setErrors)}
                        </Stack>
                        <Stack direction={isMobile() ? "column" : "row"} spacing={2}>
                            {getTextField('ミドルネーム', 'name_middle', info, setInfo, errors, setErrors)}
                            {getTextField('ニックネーム', 'nickname', info, setInfo, errors, setErrors)}
                        </Stack>
                        <Stack direction={isMobile() ? "column" : "row"} spacing={2}>
                            {getTextField('メールアドレス', 'email', info, setInfo, errors, setErrors)}
                            {getTextField('電話番号', 'phone', info, setInfo, errors, setErrors)}
                        </Stack>
                        <Stack direction={isMobile() ? "column" : "row"} spacing={2}>
                            {getEmpCdField(info, setInfo, errors, setErrors, employments, storeList)}
                        </Stack>
                        <Stack direction={isMobile() ? "column" : "row"} spacing={2}>
                            {getEmploymentTypeSelect(info, setInfo, canEditTargetUser)}
                            {getNumberField('時給', 'hourly_wage', info, setInfo, !canEditTargetUser(), { step: 1, min: 0 }, { nonNegativeInteger: true })}
                        </Stack>
                        {getNumberField('最大労働時間', 'monthly_max_hours', info, setInfo, !canEditTargetUser())}
                        <Stack direction={isMobile() ? "column" : "row"} spacing={2}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        style={{ marginLeft: '-0.75rem' }}
                                        checked={info.view_only}
                                        onChange={e => setInfo({ ...info, view_only: e.target.checked })}
                                    />
                                }
                                label="正社員(間接)"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        style={{ marginLeft: '-0.75rem' }}
                                        checked={info.is_student}
                                        onChange={e => setInfo({ ...info, is_student: e.target.checked })}
                                    />
                                }
                                label="高校生"
                            />
                        </Stack>
                        {isMasterAdmin && (
                            <>
                                <FormControl sx={{ width: isMobile() ? '90%' : '50%' }}>
                                    <InputLabel>会社</InputLabel>
                                    <Select
                                        value={info.company_id || ''}
                                        onChange={e => {
                                            const newCompanyId = e.target.value
                                            // Validate that all user's stores belong to the new company
                                            const userStores = storeList.filter(s => info.store_ids?.includes(s.id))
                                            const storesNotInNewCompany = userStores.filter(s => s.company?.id !== newCompanyId)
                                            
                                            if (storesNotInNewCompany.length > 0 && newCompanyId) {
                                                const storeNames = storesNotInNewCompany.map(s => s.name).join(', ')
                                                setSnackbarSeverity('error')
                                                setSnackbarMessage(`先に店舗を変更してください。以下の店舗は選択した会社に属していません: ${storeNames}`)
                                                return
                                            }
                                            
                                            // Block changing to default company if emp_cd has a prefix
                                            const newCompany = companies.find(c => c.id === newCompanyId)
                                            const empCd = String(info?.emp_cd ?? '')
                                            const hasPrefixFormat = empCd.includes('-')
                                            
                                            if (newCompany?.default_company && hasPrefixFormat) {
                                                setSnackbarSeverity('error')
                                                setSnackbarMessage('先に社員コードからプレフィックスを削除してください。')
                                                return
                                            }
                                            
                                            setInfo({ ...info, company_id: newCompanyId })
                                        }}
                                        label="会社"
                                    >
                                        <MenuItem value="">未選択</MenuItem>
                                        {companies.map(company => (
                                            <MenuItem key={company.id} value={company.id}>
                                                {company.company_name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            style={{ marginLeft: '-0.75rem' }}
                                            checked={info.company_admin || false}
                                            onChange={e => setInfo({ ...info, company_admin: e.target.checked })}
                                        />
                                    }
                                    label="会社管理者"
                                />
                            </>
                        )}
                        {isAdmin && <AdminManagerButtons 
                            info={info}
                            userId={userId}
                            setSnackbarMessage={setSnackbarMessage}
                            setSnackbarSeverity={setSnackbarSeverity}
                            fetch={fetch}
                            setNewPass={setNewPass}
                            setIsLoading={setIsLoading}
                            history={history}
                            employmentId={employmentId}
                            isAdmin={isAdmin}
                        />}
                    </Stack>
                </Card>
                <Box style={{ width: isMobile() ? '90vw' : '45%', marginBottom: isMobile() && '4rem', display: "flex", flexDirection: "column", alignItems: "center" }}>
                    {employments?.length ? employments.map(e => {
                        return (
                            <Card key={e.id} sx={{ p: isMobile() ? 3 : 2, width: isMobile() ? '90vw' : '100%', boxShadow: isMobile() && 'none', mb: '0.25rem'}} >
                                <EmploymentCard 
                                    employment={e}
                                    employments={employments}
                                    setEmployments={setEmployments}
                                    info={info}
                                    setSnackbarMessage={setSnackbarMessage}
                                    setSnackbarSeverity={setSnackbarSeverity}
                                    onDelete={handleDeleteEmployment}
                                    employmentId={employmentId}
                                />
                            </Card>
                        )
                    }) : <Typography>所属店舗情報がありません。</Typography>}
                    {addingStore ? (
                        <Box sx={{ width: '100%', mt: '0.5rem', mb: '5rem' }}>
                            <Autocomplete
                                options={filteredStores}
                                getOptionLabel={option => option.seg_code + ' - ' + option.name}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                onChange={(_, newValue) => setSelectedStore(newValue)}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        label="店舗名"
                                        placeholder="選択してください"
                                        InputLabelProps={{ shrink: true }}
                                    />
                                )}
                            />
                            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                <Button
                                    variant="contained"
                                    color="paloBlue"
                                    disabled={!selectedStore}
                                    onClick={handleAddStore}
                                    sx={{ flex: 1 }}
                                >
                                    追加する
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={() => { setAddingStore(false); setSelectedStore(null) }}
                                    sx={{ flex: 1 }}
                                >
                                    キャンセル
                                </Button>
                            </Stack>
                        </Box>
                    ) : (
                        <Button variant="outlined" color="black" sx={{ mt: '0.5rem', width: '100%', mb: '5rem' }} onClick={() => setAddingStore(true)}>
                            <AddOutlined sx={{ mr: '.5rem' }} />
                            店舗を追加する
                        </Button>
                    )}
                </Box>
            </Stack>
            <Box style={{ backgroundColor: 'white', width: '100%', position: 'fixed', bottom: 0, marginLeft: '-1rem', zIndex: isMobile() && 1001, marginBottom: isMobile() && '3rem' }}>
                <Divider />
                <Box style={{ display: 'flex', justifyContent: 'left', margin: '1rem' }}>
                    <Button variant='contained' sx={{ ml: 2 }} onClick={handleSaveClick} disabled={!editing}>
                        変更して保存する
                    </Button>
                    <Button variant='outlined' sx={{ ml: 2 }} onClick={handleReturn}>
                        キャンセル
                    </Button>
                </Box>
                {isLoading && <CircularProgress sx={{ mx: '50%', mt: '10%' }} />}
            </Box>
        </Box>
    )
}

export default AdminUserManagementEdit
